import { Button, Checkbox, InlineNotification } from '@carbon/react';
import {
  DropdownField,
  NumberField,
  TextAreaField,
  TextField,
} from 'components/form-fields-2';
import { QuarterCalls } from 'components/quarter-calls';
import { SurveyAreaAutocomplete } from 'components/survey-area-autocomplete';
import { useAtomValue } from 'jotai';
import { ReactNode, useCallback, useState } from 'react';
import type { ReferenceLocationResponse } from 'types/api-responses';
import { tractEnumLoadableAtom } from '../atoms';
import {
  allowedValuesFor,
  fixedNumber,
  fixedPercentHint,
  formatTitleRiskValue,
  isLockedField,
  isRuledField,
  isUnusedField,
  titleQualityItems,
  titleRiskFactorItems,
} from '../utils';
import type { EntityAccountSelection } from '../entity-account-picker';
import { EntityAccountField } from './entity-account-field';
import styles from './bulk-upload.module.scss';
import {
  FIELD_LABELS,
  REQUIRED_FIELDS,
  asList,
  asText,
  displayValue,
  isBlank,
} from './fields';
import type { FieldKey, FieldValue, RowValues } from './types';

type Options = Array<{ value: string; label: string }>;

const label = (key: FieldKey) =>
  `${FIELD_LABELS[key]}${REQUIRED_FIELDS.includes(key) ? '*' : ''}`;

const asNumber = (value: FieldValue | undefined) => {
  return typeof value === 'number' ? value : null;
};

interface AssetFormProps {
  values: RowValues;
  errors: Partial<Record<FieldKey, string>>;
  /** The survey area the server resolved for the saved values, if any. */
  referenceLocationId: number | null;
  /** A state setter: takes an update of the current values. */
  onChange: (update: (current: RowValues) => RowValues) => void;
}

/**
 * Every asset field the single-asset wizard has, bound to one row's values.
 * Enum options come from GET /crm/enums; the server re-validates everything.
 * @param {AssetFormProps} props - Values, errors to show, and onChange
 * @return {JSX.Element} The form grid
 */
export const AssetForm = ({
  values,
  errors,
  referenceLocationId,
  onChange,
}: AssetFormProps) => {
  const enums = useAtomValue(tractEnumLoadableAtom);
  const options = enums.state === 'hasData' ? enums.data : null;
  const [choosingEntity, setChoosingEntity] = useState(false);

  const set = useCallback(
    (patch: RowValues) => onChange((current) => ({ ...current, ...patch })),
    [onChange]
  );
  const setEntityAccount = useCallback(
    ({ entityId, accountId }: EntityAccountSelection) =>
      set({
        insightsEntity: entityId === null ? null : String(entityId),
        crmAccount: accountId || null,
      }),
    [set]
  );
  const invalid = (key: FieldKey) => {
    return errors[key] ? { invalid: true, invalidText: errors[key] } : {};
  };
  // The server's rules for the row's interest type, as the wizard follows
  // them: a field the type does not carry is disabled, as is a number it
  // fixes. A value left in one is dropped by the server, with a warning.
  const allowed = allowedValuesFor(
    options?.instrumentType,
    asText(values.instrumentType)
  );
  // Only the units the type allows are offered (NMA for OGL and UMI, NA for
  // Surface, Surface Lease and Commission); a unit the row already holds that
  // the type does not allow stays listed, beside its error, until it is
  // changed.
  const unitOptions = (options?.netAcreageUnit ?? []).filter(
    (unit) =>
      !allowed?.netAcreageUnits ||
      allowed.netAcreageUnits.includes(unit.value) ||
      unit.value === asText(values.netAcreageUnits)
  );

  const dropdown = (
    key: FieldKey,
    items: Options,
    { optional = true, disabled = false } = {}
  ) => (
    <DropdownField<string>
      id={`bulk-asset-${key}`}
      titleText={label(key)}
      label="Choose an option"
      items={[...(optional ? [''] : []), ...items.map((item) => item.value)]}
      itemToString={(item) => {
        if (item === '') return 'None';
        return (
          items.find((option) => option.value === item)?.label ?? item ?? ''
        );
      }}
      value={asText(values[key])}
      onChange={(value) => set({ [key]: value || null } as RowValues)}
      disabled={disabled}
      {...invalid(key)}
    />
  );

  // A number the type fixes shows that value, as the server will save it.
  const number = (key: FieldKey) => (
    <NumberField
      id={`bulk-asset-${key}`}
      label={label(key)}
      value={
        (isRuledField(key) ? fixedNumber(allowed, key) : undefined) ??
        asNumber(values[key])
      }
      onChange={(value) => set({ [key]: value } as RowValues)}
      disabled={isRuledField(key) && isLockedField(allowed, key)}
      helperText={
        isRuledField(key)
          ? fixedPercentHint(allowed, key, asText(values.instrumentType))
          : undefined
      }
      {...invalid(key)}
      {...(errors[key] &&
      !isBlank(values[key]) &&
      typeof values[key] !== 'number'
        ? {
            invalidText: `${errors[key]} (uploaded as "${asText(
              values[key]
            )}")`,
          }
        : {})}
    />
  );

  const text = (key: FieldKey) => (
    <TextField
      id={`bulk-asset-${key}`}
      labelText={label(key)}
      value={asText(values[key])}
      onChange={(value) => set({ [key]: value } as RowValues)}
      {...invalid(key)}
    />
  );

  const textArea = (key: FieldKey) => (
    <TextAreaField
      id={`bulk-asset-${key}`}
      labelText={label(key)}
      value={asText(values[key])}
      onChange={(value) => set({ [key]: value } as RowValues)}
      {...invalid(key)}
    />
  );

  const surveyArea = values.surveyArea
    ? ({
        id: asNumber(values.surveyAreaId) ?? referenceLocationId ?? 0,
        name: asText(values.surveyArea),
      } as ReferenceLocationResponse)
    : undefined;
  const surveyAreaError = errors.surveyArea ?? errors.surveyAreaId;
  const entityError = errors.insightsEntity ?? errors.crmAccount;
  const hasOwnEntity =
    !isBlank(values.insightsEntity) || !isBlank(values.crmAccount);

  const cell = (node: ReactNode, wide = false) => (
    <div className={wide ? styles.formCellWide : styles.formCell}>{node}</div>
  );

  return (
    <div className={styles.formGrid}>
      {cell(
        dropdown('instrumentType', options?.instrumentType ?? [], {
          optional: false,
        })
      )}
      {cell(dropdown('tractSource', options?.tractSource ?? []))}
      {cell(
        <SurveyAreaAutocomplete
          id="bulk-asset-surveyArea"
          titleText={label('surveyArea')}
          placeholder=""
          location={
            surveyAreaError
              ? {
                  value: surveyArea,
                  isDirty: true,
                  isValid: false,
                  error: surveyAreaError,
                }
              : { value: surveyArea, isDirty: true, isValid: true }
          }
          onChange={({ selectedItem }) =>
            set(
              selectedItem
                ? {
                    surveyArea: selectedItem.name,
                    surveyAreaId: selectedItem.id,
                  }
                : { surveyArea: null, surveyAreaId: null }
            )
          }
          additionalPayload={{ requireSalesforceLandGridTract: true }}
          // An empty search counts every land-grid survey area, about 8 s in
          // production, before the field can show anything.
          searchMinChars={2}
        />
      )}
      {cell(
        <QuarterCalls
          id="bulk-asset-quarterCalls"
          labelText={label('quarterCalls')}
          placeholder="Quarter Call"
          items={asList(values.quarterCalls)}
          onAdd={(call) =>
            set({ quarterCalls: [...asList(values.quarterCalls), call] })
          }
          onRemove={(call) =>
            set({
              quarterCalls: asList(values.quarterCalls).filter(
                (item) => item !== call
              ),
            })
          }
          invalid={!!errors.quarterCalls}
          invalidText={errors.quarterCalls}
        />
      )}
      {cell(textArea('tractFullLegalDescription'), true)}
      {cell(text('lots'))}
      {cell(
        <Checkbox
          id="bulk-asset-gridException"
          labelText={label('gridException')}
          checked={values.gridException === true}
          onChange={(_event, { checked }) => set({ gridException: checked })}
        />
      )}
      {cell(number('grossAcres'))}
      {cell(number('tractNetAcres'))}
      {cell(
        dropdown('netAcreageUnits', unitOptions, {
          optional: false,
        })
      )}
      {cell(dropdown('recordType', options?.tractRecordType ?? []))}
      {cell(number('leaseRoyaltyRate'))}
      {cell(
        dropdown('leaseRoyaltyType', options?.royaltyType ?? [], {
          disabled: isUnusedField(allowed, 'leaseRoyaltyType'),
        })
      )}
      {cell(number('royaltyBurdens'))}
      {cell(number('orriBurdens'))}
      {cell(text('alternateAssetId'))}
      {cell(
        dropdown(
          'titleQuality',
          titleQualityItems.map((value) => ({
            value,
            label: formatTitleRiskValue(value, 'None'),
          })),
          { optional: false }
        )
      )}
      {cell(
        dropdown(
          'titleRiskFactor',
          titleRiskFactorItems.map((value) => ({
            value,
            label: formatTitleRiskValue(value, 'None'),
          })),
          { optional: false }
        )
      )}
      {cell(textArea('depthFullDescription'), true)}
      {cell(number('startDepth'))}
      {cell(number('endDepth'))}
      {cell(textArea('notes'), true)}
      {cell(
        <section
          className={styles.entitySection}
          aria-label="Insights Entity and CRM Account"
        >
          <h4 className={styles.entitySectionTitle}>
            Insights Entity and CRM Account
          </h4>
          {hasOwnEntity ? (
            <dl className={styles.entitySectionValues}>
              <dt>{FIELD_LABELS.insightsEntity}</dt>
              <dd>{displayValue(values.insightsEntity)}</dd>
              <dt>{FIELD_LABELS.crmAccount}</dt>
              <dd>{displayValue(values.crmAccount)}</dd>
            </dl>
          ) : (
            <p className={styles.entitySectionValue}>Uses the batch default.</p>
          )}
          {entityError && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title={entityError}
            />
          )}
          <div className={styles.entitySectionActions}>
            <Button
              kind="tertiary"
              size="sm"
              onClick={() => setChoosingEntity((open) => !open)}
            >
              {choosingEntity ? 'Done' : 'Choose entity and account'}
            </Button>
            {hasOwnEntity && (
              <Button
                kind="ghost"
                size="sm"
                onClick={() => set({ insightsEntity: null, crmAccount: null })}
              >
                Use batch default
              </Button>
            )}
          </div>
          {choosingEntity && <EntityAccountField onChange={setEntityAccount} />}
        </section>,
        true
      )}
    </div>
  );
};
